// /guides — the project's SAVED GUIDES, first-class: in-product spotlight
// tours (`type:'guide'` fork-vault entries, tour JSON in the TutorialFlow
// schema) presented with the SAME Drive-style idiom as the Apps and Agents
// pages: a collapsible left rail of guide rows (grouped by the app each guide
// explains), hover actions + a per-row ⋯ menu, a rail-footer "Build a guide"
// button that opens the shared AppRequestDialog (POST /api/app_requests,
// kinds new_guide | change_guide), and a MAIN PANE that — unlike Agents,
// whose main pane is only a welcome card — renders a real PREVIEW of the
// selected guide: its step outline, read from the tour JSON that is already
// on the client. Launching is explicit (the Launch button / the row's ▶),
// so a click can inspect before it spotlights.
//
// The list itself comes from the existing tutorial plumbing
// (loadProjectGuides — the docs-engine hop to the fork vault); this page adds
// no new read surface. Deleting goes through the docs-engine delete action,
// which the backend allowlists for guide-* slugs only.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GraduationCap, Loader2, RefreshCw, AppWindow, Play, Sparkles, Wrench,
  Trash2, MoreVertical, ClipboardList, PanelLeftClose, PanelLeftOpen,
  MousePointerClick, Navigation, History, FileDown,
} from "lucide-react";
import { loadProjectGuides, launchGuide, deleteGuide, invalidateGuides, listGuidePdfs, downloadGuidePdf } from "../components/tutorial/guides";
import VersionHistory from "../components/VersionHistory";
import { listAppRequests } from "../services/apps";
import { createPageUrl } from "@/utils";
import { useProject } from "@/contexts/ProjectContext";
import AppRequestDialog from "../components/AppRequestDialog";
import { isPortal } from "../utils/portal";

const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";

// Kind metadata for the shared AppRequestDialog. new_guide takes a title +
// details brief; change_guide targets an existing guide by its vault slug —
// a string, so it rides in the details text (textTargets, the change_source
// precedent) rather than as an id column.
const guideRequestKinds = (guides) => [
  { kind: "new_guide", label: "New guide", icon: Sparkles,
    hint: "Describe the walkthrough you need — an agent will build the tour and it becomes part of this project.",
    titlePlaceholder: "What should the guide explain?",
    detailsPlaceholder: "Describe in plain language what the guide should walk through — which app, pages or process, who it's for, what the reader should be able to do afterwards…" },
  { kind: "change_guide", label: "Change guide", icon: Wrench,
    hint: "Build a change into an existing guide.",
    textTargets: guides.map((g) => ({ value: `${g.__guide?.slug} — ${g.title}`, label: g.title })),
    textTargetPrefix: "TARGET GUIDE",
    emptyTitle: "No guides to change yet",
    emptyHint: "This project has no saved guides. Build a new one first.",
    emptyActionKind: "new_guide", emptyActionLabel: "Build a new guide",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what should change about this guide — steps to add or drop, copy to fix, screens it should now cover…" },
];

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent hover:bg-card-border/30 " +
        (danger ? "text-red-600" : "text-page-text")
      }
    >
      <Icon size={13} className="opacity-70" /> {label}
    </button>
  );
}

// One step of the preview outline: title + where the tour goes (page) and
// what it spotlights (target) — read straight from the step object.
function StepLine({ step, index }) {
  return (
    <li className="flex items-start gap-3 py-2 border-b border-card-border/60 last:border-b-0">
      <span className="shrink-0 w-6 h-6 rounded-full bg-nav-active/10 text-nav-active text-xs font-semibold flex items-center justify-center mt-0.5">
        {index + 1}
      </span>
      <span className="min-w-0 flex flex-col">
        <span className="text-sm font-medium text-page-text">{step.title || `Step ${index + 1}`}</span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
          {step.page && (
            <span className="inline-flex items-center gap-1 text-[11px] text-page-muted">
              <Navigation size={10} className="opacity-70" /> {typeof step.page === "string" ? step.page : "(dynamic page)"}
            </span>
          )}
          {step.target && (
            <span className="inline-flex items-center gap-1 text-[11px] text-page-muted">
              <MousePointerClick size={10} className="opacity-70" />
              <code className="text-[10px]">{Array.isArray(step.target) ? step.target[0] : step.target}</code>
            </span>
          )}
          {!step.page && !step.target && (
            <span className="text-[11px] text-page-muted">centered card</span>
          )}
        </span>
      </span>
    </li>
  );
}

export default function Guides() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug, sharedSurfaceOnly } = useProject();
  // Someone who reaches this project only through a shared app/agent cannot
  // build anything in it (the request would 403), so don't offer to.
  const canBuild = !isPortal() && !sharedSurfaceOnly;

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [menuGuideSlug, setMenuGuideSlug] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null); // guide being deleted
  const [historyTarget, setHistoryTarget] = useState(null); // guide whose Version history is open
  const [busy, setBusy] = useState(false);
  // slug -> stored PDF metadata (modules/guides). A guide's PDF is rendered
  // when the guide is built; the page only offers the stored file.
  const [pdfs, setPdfs] = useState(() => new Map());
  const [pdfBusy, setPdfBusy] = useState(null); // slug being downloaded
  const [pdfErr, setPdfErr] = useState(null);
  // Request dialog: { kind, guide? } — shared AppRequestDialog machinery.
  const [requestDialog, setRequestDialog] = useState(null);
  // Open (queued/claimed/running) request count for the rail badge.
  const [openRequests, setOpenRequests] = useState(0);

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("guidesRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("guidesRailOpen", n ? "1" : "0"); } catch {} return n; });

  const load = (force = false) => {
    setLoading(true);
    setErr(null);
    if (force) invalidateGuides(projectSlug);
    loadProjectGuides(projectSlug)
      .then((tours) => setGuides(tours || []))
      .catch((e) => {
        const msg = e?.message || String(e);
        setErr(/no access to project/i.test(msg) ? "You don't have access to this project's guides." : msg);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [projectSlug]);
  const loadPdfs = () => {
    if (!projectSlug) return;
    listGuidePdfs().then(setPdfs).catch(() => setPdfs(new Map()));
  };
  useEffect(() => { loadPdfs(); }, [projectSlug, loading]);

  const downloadPdf = async (g) => {
    const slug = g?.__guide?.slug;
    if (!slug || !pdfs.get(slug)) return;
    setPdfBusy(slug); setPdfErr(null);
    try { await downloadGuidePdf(slug); }
    catch (e) { setPdfErr(e?.message || String(e)); }
    finally { setPdfBusy(null); }
  };
  const pdfLabel = (p) => {
    if (!p) return null;
    const when = p.created_at ? new Date(p.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
    const pages = p.pages ? `${p.pages} page${p.pages === 1 ? "" : "s"}` : "";
    return [pages, when ? `captured ${when}` : ""].filter(Boolean).join(" · ");
  };

  useEffect(() => {
    let alive = true;
    listAppRequests({ status: "open" })
      .then((r) => { if (alive) setOpenRequests((r?.requests || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [requestDialog]);

  // Close the row menu on outside click (matches Apps/Agents).
  useEffect(() => {
    if (menuGuideSlug == null) return;
    const close = () => setMenuGuideSlug(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuGuideSlug]);

  // Deep link: ?guide=<vault slug> (the finish alert / My Builds URL) —
  // select AND launch that tour once the list is in. One shot per navigation.
  const launchedRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const wanted = new URLSearchParams(location.search || "").get("guide");
    if (!wanted || launchedRef.current === wanted) return;
    if (guides.some((g) => g.__guide?.slug === wanted)) {
      launchedRef.current = wanted;
      setSelectedSlug(wanted);
      launchGuide(projectSlug, wanted);
    }
  }, [loading, guides, location.search, projectSlug]);

  const selected = guides.find((g) => g.__guide?.slug === selectedSlug) || null;

  // Rail grouping: one section per app the guides explain, plus "Project"
  // for general guides (no app binding). Guides have no owner/visibility
  // model (they are project-level vault entries), so — unlike Agents — the
  // sections describe SUBJECT, not access.
  const groups = useMemo(() => {
    const byApp = new Map();
    for (const g of guides) {
      const key = g.__guide?.app || g.app || null;
      if (!byApp.has(key)) byApp.set(key, []);
      byApp.get(key).push(g);
    }
    const appKeys = [...byApp.keys()].filter(Boolean).sort();
    const out = appKeys.map((k) => ({ label: k, app: k, items: byApp.get(k) }));
    if (byApp.has(null)) out.push({ label: "Project", app: null, items: byApp.get(null) });
    return out;
  }, [guides]);

  const launch = (g) => { if (g?.__guide?.slug) launchGuide(projectSlug, g.__guide.slug); };
  const openApp = (viewName) => navigate(createPageUrl(`apps/${encodeURIComponent(viewName)}`));

  const doRemove = async (g) => {
    setBusy(true);
    try {
      await deleteGuide(projectSlug, g.__guide.slug);
      setGuides((prev) => prev.filter((x) => x.__guide?.slug !== g.__guide.slug));
      if (selectedSlug === g.__guide.slug) setSelectedSlug(null);
      setRemoveTarget(null);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  };

  const GuideRow = (g) => {
    const slug = g.__guide?.slug;
    const menuOpen = menuGuideSlug === slug;
    const isSelected = selectedSlug === slug;
    return (
      <div
        key={slug || g.key}
        className={
          "group relative rounded-lg border transition-colors mb-1 " +
          (isSelected ? "border-nav-active bg-nav-active/5" : "border-transparent hover:border-card-border hover:bg-card-border/20")
        }
      >
        <div className="flex items-center">
          <button
            type="button"
            data-testid={`guide-row-${slug}`}
            onClick={() => setSelectedSlug(slug)}
            title="Preview this guide"
            className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-inherit"
          >
            <GraduationCap size={16} className={"shrink-0 " + (isSelected ? "text-nav-active" : "text-page-muted")} />
            <span className="min-w-0 flex flex-col">
              <span className="text-sm text-page-text overflow-hidden text-ellipsis whitespace-nowrap">{g.title}</span>
              <span className="text-[11px] text-page-muted">{g.steps.length} steps</span>
            </span>
          </button>
          <button
            type="button"
            data-testid={`guide-play-${slug}`}
            title="Launch"
            onClick={(e) => { e.stopPropagation(); launch(g); }}
            className="shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100"
          >
            <Play size={14} />
          </button>
          {canBuild && (<button
            type="button"
            data-testid={`guide-menu-${slug}`}
            title="More"
            onClick={(e) => { e.stopPropagation(); setMenuGuideSlug(menuOpen ? null : slug); }}
            className={
              "shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100 " +
              (menuOpen ? "opacity-100" : "")
            }
          >
            <MoreVertical size={14} />
          </button>)}
        </div>
        {menuOpen && (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-52 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem icon={Play} label="Launch" onClick={() => { setMenuGuideSlug(null); launch(g); }} />
            {pdfs.get(slug) && (
              <MenuItem icon={FileDown} label="Download PDF" onClick={() => { setMenuGuideSlug(null); downloadPdf(g); }} />
            )}
            <MenuItem icon={Wrench} label="Build a change" onClick={() => {
              setMenuGuideSlug(null);
              setRequestDialog({ kind: "change_guide", guide: g });
            }} />
            <MenuItem icon={History} label="Version history" onClick={() => { setMenuGuideSlug(null); setHistoryTarget(g); }} />
            <MenuItem icon={Trash2} label="Delete guide" danger onClick={() => { setMenuGuideSlug(null); setRemoveTarget(g); }} />
          </div>
        )}
      </div>
    );
  };

  const Section = ({ label, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex h-full min-h-0" data-tour="page-guides">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="guides-rail-open"
            onClick={toggleRail}
            title="Show guides list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <GraduationCap size={16} className="text-nav-active mt-1" />
        </div>
      )}
      {/* Left rail: the Drive-like guides explorer (collapsible), mirroring Apps/Agents */}
      <aside className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <GraduationCap size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Guides</span>
          {canBuild && (<button
            type="button"
            data-testid="guides-requests-badge"
            title="Open builds"
            className="ml-auto relative text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
            onClick={() => navigate(createPageUrl("board"))}
          >
            <ClipboardList size={15} />
            {openRequests > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-nav-active text-white text-[9px] leading-[14px] text-center">
                {openRequests}
              </span>
            )}
          </button>)}
          <button
            type="button"
            data-testid="guides-refresh"
            onClick={() => load(true)}
            title="Refresh"
            className={(canBuild ? "" : "ml-auto ") + "text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            type="button"
            data-testid="guides-rail-close"
            onClick={toggleRail}
            title="Collapse guides list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading guides…
            </div>
          )}
          {!loading && err && <div className="text-sm text-red-500 px-2 py-3">{err}</div>}
          {!loading && !err && guides.length === 0 && (
            <div className="text-sm text-page-muted px-2 py-3">
              No guides yet in this project. Build one below.
            </div>
          )}
          {!loading && !err && groups.map((grp) => (
            <Section key={grp.app || "project"} label={grp.label}>
              {grp.items.map(GuideRow)}
            </Section>
          ))}
        </div>
        {/* Rail footer: file a request for a new guide (agent-built). */}
        <div className="p-2 border-t border-card-border">
          {canBuild && (<button
            type="button"
            data-testid="guides-build"
            onClick={() => setRequestDialog({ kind: "new_guide" })}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
          >
            <Sparkles size={14} /> Build a guide
          </button>)}
        </div>
      </aside>

      {/* Main: the selected guide's PREVIEW (step outline + Launch), or the
          welcome card when nothing is selected. */}
      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg overflow-y-auto">
        {!selected ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <GraduationCap size={40} className="text-page-muted mb-3" />
            <div className="text-page-text font-medium">Your project guides</div>
            <div className="text-sm text-page-muted mt-1 max-w-md">
              Guides are step-by-step spotlight tours through this project's apps and
              pages. Pick one from the left to preview its steps, then launch it to
              get a live walkthrough of the actual UI.
            </div>
            {canBuild && (<button
              type="button"
              data-testid="guides-build-main"
              onClick={() => setRequestDialog({ kind: "new_guide" })}
              className={btnGhost + " mt-4"}
            >
              <Sparkles size={14} /> Build a guide
            </button>)}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto px-6 py-8" data-testid="guide-preview">
            <div className="flex items-start gap-3 mb-1">
              <GraduationCap size={24} className="text-nav-active mt-0.5 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-page-text m-0">{selected.title}</h1>
                {selected.description && (
                  <p className="text-sm text-page-muted mt-1 mb-0">{selected.description}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4 mb-6 pl-9">
              <button
                type="button"
                data-testid={`guide-launch-${selected.__guide?.slug}`}
                className={btnPrimary}
                onClick={() => launch(selected)}
              >
                <Play size={14} /> Launch tour
              </button>
              {(() => {
                const slug = selected.__guide?.slug;
                const p = pdfs.get(slug);
                const stale = !!(p?.guide_updated && selected.__guide?.updated && new Date(selected.__guide.updated) > new Date(p.guide_updated));
                return (
                  <button
                    type="button"
                    data-testid={`guide-pdf-${slug}`}
                    data-pdf-state={p ? (stale ? "stale" : "ready") : "none"}
                    className={btnGhost + (p ? "" : " opacity-60 cursor-not-allowed")}
                    disabled={!p || pdfBusy === slug}
                    title={p
                      ? `Download this guide as a PDF — every step's text and a screenshot of what it shows, ${pdfLabel(p)}${stale ? " (the guide changed after this capture)" : ""}`
                      : "No PDF yet — it is rendered when the guide is built"}
                    onClick={() => downloadPdf(selected)}
                  >
                    {pdfBusy === slug ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                    Download PDF
                    {p && <span className="text-[11px] text-page-muted" data-testid={`guide-pdf-meta-${slug}`}>{pdfLabel(p)}</span>}
                  </button>
                );
              })()}
              {canBuild && (
                <button type="button" className={btnGhost} onClick={() => setRequestDialog({ kind: "change_guide", guide: selected })}>
                  <Wrench size={14} /> Build a change
                </button>
              )}
              {canBuild && (
                <button type="button" className={btnGhost + " text-red-600"} onClick={() => setRemoveTarget(selected)}>
                  <Trash2 size={14} /> Delete
                </button>
              )}
              {(selected.__guide?.app || selected.app) && (
                <button
                  type="button"
                  data-testid={`guide-app-${selected.__guide?.app || selected.app}`}
                  title="Open the app this guide explains"
                  onClick={() => openApp(selected.__guide?.app || selected.app)}
                  className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2.5 py-1 cursor-pointer hover:bg-card-border/30"
                >
                  <AppWindow size={11} className="opacity-70" />
                  <span className="max-w-[180px] truncate">{selected.__guide?.app || selected.app}</span>
                </button>
              )}
            </div>
            {pdfErr && <div className="text-sm text-red-600 mb-3 pl-9" data-testid="guide-pdf-error">{pdfErr}</div>}
            <div className="rounded-lg border border-card-border bg-card-bg px-4 py-2">
              <ol className="list-none m-0 p-0">
                {selected.steps.map((s, i) => <StepLine key={i} step={s} index={i} />)}
              </ol>
            </div>
          </div>
        )}
      </main>

      {requestDialog && (
        <AppRequestDialog
          kinds={guideRequestKinds(guides)}
          title="Build a guide"
          preset={{
            kind: requestDialog.kind,
            title: requestDialog.guide ? `Change guide ${requestDialog.guide.title}` : "",
          }}
          onClose={() => setRequestDialog(null)}
          onCreated={() => setRequestDialog(null)}
        />
      )}

      {historyTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setHistoryTarget(null)}>
          <div className="w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-lg bg-card-bg" onClick={(e) => e.stopPropagation()}>
            <VersionHistory
              kind="guide"
              name={historyTarget.__guide?.slug}
              title={`Version history — ${historyTarget.title}`}
              canRestore={canBuild}
              projectSlug={projectSlug}
              onRestored={() => { invalidateGuides(projectSlug); load(true); }}
            />
          </div>
        </div>
      )}

      {removeTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setRemoveTarget(null)}>
          <div className="w-full max-w-sm rounded-lg border border-card-border bg-card-bg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold text-page-text mb-1">Delete guide?</div>
            <div className="text-sm text-page-muted mb-4">
              "{removeTarget.title}" will be removed from this project for everyone. This cannot be undone.
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} disabled={busy} onClick={() => setRemoveTarget(null)}>Cancel</button>
              <button
                type="button"
                data-testid="guide-delete-confirm"
                className={btnPrimary + " bg-red-600"}
                disabled={busy}
                onClick={() => doRemove(removeTarget)}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
